import { useEffect, useRef, useState } from 'react';
import { localeUI } from '../lib/format';
import { Ban, Home, Trash2, Wrench, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ScheduleModal } from './ScheduleModal';

/**
 * Day-grid de Scheduling (réplica del DayBoard de ClaimRoute): eje de horas a la
 * izquierda + una columna por inspector, con las citas como bloques posicionados por
 * hora y coloreados por inspector. Compartido por Calendar (Day) y Scheduled (Board).
 */
export type GridAppt = {
  id: string; claim_id: string | null; inspector_name: string | null; contractor_name?: string | null;
  type: string; progress: string; scheduled_start: string | null; scheduled_end?: string | null;
  address: string | null; client_name: string | null;
};
type Weather = { icon: string; short: string; prob: number };
/** Tramo hasta una cita (de la propuesta de Optimize routes): cuánto se tarda desde el punto anterior. */
export type GridDrive = { driveMin: number | null; miles: number | null; prev: string | null; prevKind: 'start' | 'stop' | 'end' | null; slackMin: number | null; estimated: boolean };
/** Vista previa de "Optimize routes": cómo quedaría una cita (inspector/hora) o si sale del día. */
export type GridOverlay = { kind: string; inspector_name: string | null; scheduled_start: string | null; from: string | null; fromStart: string; out: boolean;
  /** Color del cambio (las dos mitades de un intercambio comparten color). */
  color?: string };

export const COLORS = [
  { bg: '#dbe7fb', ink: '#2f5fb0' }, { bg: '#d7f0df', ink: '#2c8a52' }, { bg: '#ffe2cf', ink: '#c2683a' },
  { bg: '#e7ddfb', ink: '#6b46c1' }, { bg: '#fdf0cf', ink: '#9a7a1e' }, { bg: '#d2f0ee', ink: '#2a8f86' },
  { bg: '#fcdcdc', ink: '#c0453f' }, { bg: '#dce7e0', ink: '#4a6b59' },
];
const DAY_START = 7, DAY_END = 20, HOUR_PX = 52;
const HOURS = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const time = (s: string | null) => (s ? new Date(s).toLocaleTimeString(localeUI(), { hour: 'numeric', minute: '2-digit' }) : '');
const hourFrac = (s: string) => { const d = new Date(s); return d.getHours() + d.getMinutes() / 60; };
const fmtHour = (h: number) => { const d = new Date(); d.setHours(h, 0, 0, 0); return d.toLocaleTimeString(localeUI(), { hour: 'numeric' }); };
const initials = (n: string) => n.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const TYPE_LABEL: Record<string, string> = { inspection: 'INSPECTION', adjuster_meeting: 'INSPECTION', atr: 'ATR', atr_on_site: 'ATR ON SITE', photos: 'PHOTOS', check_pickup: 'CHECK', material_sample: 'MATERIAL', repair_video: 'VIDEO', custom: 'CUSTOM' };

/**
 * ORDEN DE LAS COLUMNAS (dueño 2026-10-19: "Manuel Ramírez colócame de último, que casi nunca tengo
 * inspecciones"): primero los inspectores CON visitas ese día, en orden alfabético; después los que no
 * tienen ninguna, también alfabéticos; y al final las columnas especiales (Contractor, Unassigned) en el
 * orden que diga `grupo`. Así el que casi nunca sale no empuja a los demás fuera de la pantalla, y los
 * usuarios no tienen que hacer scroll a la derecha para ver a quien sí trabaja.
 */
export function ordenarInspectores(nombres: string[], cuenta: (n: string) => number, grupo: (n: string) => number = () => 0): string[] {
  const col = new Intl.Collator(undefined, { sensitivity: 'base' });
  return [...nombres].sort((a, b) =>
    grupo(a) - grupo(b)
    || Number(cuenta(a) === 0) - Number(cuenta(b) === 0)
    || col.compare(a, b));
}

export function SchedulingDayGrid({ day, appts, inspectors, insFilter, weather, onOpen, onChanged, overlay, extras, compact, drive, driveTotals }: {
  day: Date; appts: GridAppt[]; inspectors: string[]; insFilter: string;
  weather: Record<string, Weather>; onOpen: (e: React.MouseEvent, a: GridAppt) => void; onChanged?: () => void;
  /** Propuesta de "Optimize routes" pintada ENCIMA de las citas reales (no se guarda nada hasta aplicar). */
  overlay?: Map<string, GridOverlay>;
  /** Citas FANTASMA de la propuesta (ATRs de la cola que entrarían al día): se pintan, no existen. */
  extras?: GridAppt[];
  /** Versión chica para el modal de Optimize routes: horas más bajas, columnas más angostas, sin botones. */
  compact?: boolean;
  /** Tramos de la propuesta: cuánto se tarda en llegar a cada cita desde el punto anterior. */
  drive?: Map<string, GridDrive>;
  /** Manejo total del día por inspector, para el encabezado de la columna. */
  driveTotals?: Map<string, { driveMin: number; miles: number }>;
}) {
  const { t } = useI18n();
  const hourPx = compact ? 40 : HOUR_PX;
  const minAlto = compact ? 34 : 46;
  const [reassign, setReassign] = useState<GridAppt | null>(null);
  const [reschedule, setReschedule] = useState<GridAppt | null>(null);
  const [rescheduleUrgent, setRescheduleUrgent] = useState(false);
  const [removing, setRemoving] = useState('');

  // ─── ARRASTRAR Y SOLTAR (dueño 2026-10-29: "agarrar el bloque y bajarlo, o moverlo a otro inspector") ───
  // Mientras se arrastra, la cita se PINTA donde va quedando (otra hora, otra columna) sin escribir nada; al
  // soltar se pide confirmación y recién ahí se guarda por la MISMA puerta que el modal (⟳ reschedule: hora
  // e inspector, con el SMS al homeowner) o que ⇄ (reassign: solo inspector). Cuadrícula de 15 minutos; la
  // cita conserva su duración; Escape suelta sin mover. Los botones de la cita no arrastran.
  type Arrastre = { id: string; a: GridAppt; startX: number; startY: number; origMin: number; durMin: number; col: string; moved: boolean };
  const arrastre = useRef<Arrastre | null>(null);
  const colEls = useRef<Record<string, HTMLDivElement | null>>({});
  const suprimirClick = useRef(false);
  const [preview, setPreviewState] = useState<{ id: string; col: string; startMin: number } | null>(null);
  const previewRef = useRef<{ id: string; col: string; startMin: number } | null>(null);
  const setPreview = (p: { id: string; col: string; startMin: number } | null) => { previewRef.current = p; setPreviewState(p); };
  const [drop, setDrop] = useState<DropPedido | null>(null);
  const puedeArrastrar = !compact && !overlay;
  const minutosDe = (isoStr: string) => { const d = new Date(isoStr); return d.getHours() * 60 + d.getMinutes(); };
  const isoDeMin = (min: number) => { const d = new Date(day); d.setHours(0, 0, 0, 0); return new Date(d.getTime() + min * 60000).toISOString(); };
  const empezarArrastre = (e: React.PointerEvent, a: GridAppt, col: string) => {
    if (!puedeArrastrar || e.button !== 0 || !a.scheduled_start || drop) return;
    if ((e.target as HTMLElement).closest('button')) return;
    const dur = a.scheduled_end ? Math.max(15, Math.round((Date.parse(a.scheduled_end) - Date.parse(a.scheduled_start)) / 60000)) : 60;
    arrastre.current = { id: a.id, a, startX: e.clientX, startY: e.clientY, origMin: minutosDe(a.scheduled_start), durMin: dur, col, moved: false };
  };
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = arrastre.current; if (!d) return;
      const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
      if (!d.moved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      d.moved = true; e.preventDefault();
      // La columna bajo el puntero; fuera de todas, se queda en la última.
      let col = previewRef.current?.col ?? d.col;
      for (const [name, el] of Object.entries(colEls.current)) {
        const r = el?.getBoundingClientRect();
        if (r && e.clientX >= r.left && e.clientX < r.right) { col = name; break; }
      }
      const paso = 15;
      let min = Math.round((d.origMin + (dy / hourPx) * 60) / paso) * paso;
      min = Math.max(DAY_START * 60, Math.min(DAY_END * 60 - d.durMin, min));
      const prev = previewRef.current;
      if (!prev || prev.id !== d.id || prev.col !== col || prev.startMin !== min) setPreview({ id: d.id, col, startMin: min });
    };
    const terminar = () => {
      const d = arrastre.current; arrastre.current = null;
      if (!d || !d.moved) return;
      suprimirClick.current = true; setTimeout(() => { suprimirClick.current = false; }, 0);
      const p = previewRef.current;
      if (!p || p.id !== d.id) { setPreview(null); return; }
      const cambiaHora = p.startMin !== d.origMin;
      const cambiaInsp = p.col !== d.col;
      // A "Contractor" / "Unassigned" no se arrastra: para eso está ⇄ (pide el nombre del contratista).
      if ((!cambiaHora && !cambiaInsp) || (cambiaInsp && (p.col === unassigned || p.col === contractorCol))) { setPreview(null); return; }
      setDrop({ a: d.a, col: p.col, startIso: isoDeMin(p.startMin), durMin: d.durMin, cambiaHora, cambiaInsp });
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && arrastre.current) { arrastre.current = null; setPreview(null); } };
    window.addEventListener('pointermove', onMove); window.addEventListener('pointerup', terminar);
    window.addEventListener('pointercancel', terminar); window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', terminar);
      window.removeEventListener('pointercancel', terminar); window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourPx, day, drop]);

  async function removeAppt(a: GridAppt) {
    if (!window.confirm(t('sch.removeConfirm'))) return;
    setRemoving(a.id);
    try { await api.patch(`/api/appointments/${a.id}`, { status: 'canceled' }); onChanged?.(); }
    catch { alert(t('cd.propFail')); } finally { setRemoving(''); }
  }
  const colorIdx = (n: string | null) => (n ? Math.max(0, inspectors.indexOf(n)) % COLORS.length : 0);
  const unassigned = t('cr.unassigned');
  // Pedido del dueño (2026-09-20): "Unassigned" es SOLO para las citas sin inspector NI contratista.
  // Las que atiende un contratista van en su propia columna "Contractor" (con el nombre en la cita).
  const contractorCol = t('sch.kind.contractor');
  const columnaDe = (a: GridAppt) => a.inspector_name || (a.contractor_name ? contractorCol : unassigned);
  const NEUTRO = { bg: '#e6e8ee', ink: '#5b6470' };
  const colorDe = (name: string) => (name === unassigned || name === contractorCol ? NEUTRO : COLORS[colorIdx(name)]!);
  const esFantasma = (id: string) => id.startsWith('pend:');
  // RASTRO EN EL ORIGEN (reporte de Paula 2026-09-23: "no me enseña la inspección de Vlamir"): la
  // propuesta la pasaba a otra columna que quedaba fuera de pantalla y en la columna original no
  // quedaba nada. Ahora, donde estaba, queda una marca tenue que dice a dónde se fue.
  type Rastro = GridAppt & { rastroA: string | null; rastroHora: string | null };
  const esRastro = (id: string) => id.startsWith('from:');
  const rastros: Rastro[] = overlay
    ? appts.filter((a) => a.scheduled_start && iso(new Date(a.scheduled_start)) === iso(day)).flatMap((a): Rastro[] => {
      const o = overlay.get(a.id);
      if (!o || o.out) return [];
      const otroInsp = !!o.inspector_name && o.inspector_name !== a.inspector_name;
      const otraHora = !!o.scheduled_start && Date.parse(o.scheduled_start) !== Date.parse(a.scheduled_start!);
      if (!otroInsp && !otraHora) return [];
      return [{ ...a, id: `from:${a.id}`, rastroA: otroInsp ? o.inspector_name : null, rastroHora: otraHora ? o.scheduled_start : null }];
    })
    : [];
  const dayList = [...appts, ...(extras ?? [])]
    .filter((a) => a.scheduled_start && iso(new Date(a.scheduled_start)) === iso(day))
    // Con la vista previa puesta, cada cita se pinta donde la propuesta la deja (otro inspector u
    // otra hora); las que salen del día se quedan en su lugar, atenuadas, con su cartel.
    .map((a) => {
      // Arrastrando: la cita se pinta en la hora y la columna donde va el puntero (nada se guardó aún).
      if (preview && preview.id === a.id && a.scheduled_start) {
        const dur = a.scheduled_end ? Date.parse(a.scheduled_end) - Date.parse(a.scheduled_start) : 3600000;
        const start = isoDeMin(preview.startMin);
        const aOtroInspector = preview.col !== columnaDe(a) && preview.col !== unassigned && preview.col !== contractorCol;
        return { ...a, inspector_name: aOtroInspector ? preview.col : a.inspector_name, scheduled_start: start, scheduled_end: new Date(Date.parse(start) + dur).toISOString() };
      }
      const o = overlay?.get(a.id);
      if (!o || o.out) return a;
      const start = o.scheduled_start ?? a.scheduled_start;
      const dur = a.scheduled_start && a.scheduled_end ? Date.parse(a.scheduled_end) - Date.parse(a.scheduled_start) : 3600000;
      return { ...a, inspector_name: o.inspector_name ?? a.inspector_name, scheduled_start: start, scheduled_end: start ? new Date(Date.parse(start) + dur).toISOString() : a.scheduled_end };
    });
  // Columnas = TODOS los inspectores conocidos (aunque el día esté vacío) + cualquier
  // nombre extra que aparezca ese día (p.ej. "Unassigned" o un importado no listado).
  const conRastros: GridAppt[] = [...dayList, ...rastros];
  const cols = insFilter ? [insFilter] : (() => {
    const extra = dayList.map(columnaDe).filter((n) => !inspectors.includes(n));
    // Contractor antes que Unassigned, al final de los inspectores.
    const orden = (n: string) => (n === contractorCol ? 1 : n === unassigned ? 2 : 0);
    const cuenta = (n: string) => dayList.filter((a) => columnaDe(a) === n).length;
    return ordenarInspectores([...new Set([...inspectors, ...extra])], cuenta, orden);
  })();

  return (
    <div className={`db${compact ? ' db-compact' : ''}`}>
      <div className="db-inner" style={{ gridTemplateColumns: `${compact ? 44 : 56}px repeat(${cols.length || 1}, minmax(${compact ? 150 : 200}px, 1fr))` }}>
        <div className="db-corner" />
        {cols.map((name) => {
          const c = colorDe(name);
          const cnt = dayList.filter((a) => columnaDe(a) === name).length;
          const tot = driveTotals?.get(name);
          return (
            <div key={name} className="db-colhead" style={{ borderTopColor: c.ink }}>
              <span className="db-av" style={{ background: c.ink }}>{name === contractorCol ? <Wrench size={12} strokeWidth={2} /> : initials(name)}</span>
              <span className="db-name">{name}</span>
              {tot && cnt > 0 && <span className="db-drive-tot" title={`🚗 ${tot.driveMin} min · ${tot.miles} mi`}>{tot.driveMin} min</span>}
              <span className="db-count" style={{ background: c.bg, color: c.ink }}>{cnt}</span>
            </div>
          );
        })}
        <div className="db-gutter" style={{ height: HOURS.length * hourPx }}>
          {HOURS.map((h) => <div key={h} className="db-hourlabel" style={{ height: hourPx }}>{fmtHour(h)}</div>)}
        </div>
        {cols.map((name) => {
          const items = conRastros.filter((a) => columnaDe(a) === name);
          const c = colorDe(name);
          return (
            <div key={name} ref={(el) => { colEls.current[name] = el; }} className={`db-col${preview && preview.col === name ? ' db-col-target' : ''}`} style={{ height: HOURS.length * hourPx }}>
              {HOURS.map((h) => <div key={h} className="db-slot" style={{ height: hourPx }} />)}
              {(() => {
                /* CARRILES para citas SOLAPADAS (reporte 2026-08-13: dos citas a la misma hora en
                   Unassigned y solo se veía una — quedaban una ENCIMA de la otra). Se ordena por
                   inicio y cada cita toma el primer carril libre; las que se tocan se reparten el
                   ancho de la columna lado a lado.
                   El ancho se reparte POR GRUPO de citas que se pisan, no por toda la columna
                   (reporte 2026-09-20: una cita sola a las 9 salía a la mitad del ancho porque otras
                   dos se pisaban a la 1 PM en la misma columna). */
                const conRango = items
                  .filter((a) => a.scheduled_start)
                  .map((a) => {
                    const sh = hourFrac(a.scheduled_start!);
                    const eh = a.scheduled_end ? hourFrac(a.scheduled_end) : sh + 1;
                    return { a, sh, eh: Math.max(eh, sh + minAlto / hourPx) }; // altura mínima cuenta como ocupación
                  })
                  .sort((x, y) => x.sh - y.sh || x.eh - y.eh);
                type Ev = (typeof conRango)[number] & { lane: number; lanes: number };
                const conCarril: Ev[] = [];
                let grupo: Ev[] = [];
                let finGrupo = -Infinity;
                let lanes: number[] = []; // fin (eh) del último evento por carril, dentro del grupo
                const cerrarGrupo = () => { const n = Math.max(1, lanes.length); for (const e of grupo) e.lanes = n; grupo = []; lanes = []; finGrupo = -Infinity; };
                for (const ev of conRango) {
                  if (grupo.length && ev.sh >= finGrupo - 0.001) cerrarGrupo(); // ya no se pisa con nadie del grupo
                  let lane = lanes.findIndex((fin) => fin <= ev.sh + 0.001);
                  if (lane === -1) { lane = lanes.length; lanes.push(ev.eh); } else lanes[lane] = ev.eh;
                  const e: Ev = { ...ev, lane, lanes: 1 };
                  grupo.push(e); conCarril.push(e); finGrupo = Math.max(finGrupo, ev.eh);
                }
                cerrarGrupo();
                return conCarril.map(({ a, sh, eh, lane, lanes: totalLanes }) => {
                const top = Math.max(0, (sh - DAY_START) * hourPx);
                const height = Math.max(minAlto, (Math.min(eh, DAY_END) - Math.max(sh, DAY_START)) * hourPx);
                const w = weather[a.id];
                const anchoPct = 100 / totalLanes;
                const rastro = esRastro(a.id) ? (a as Rastro) : null;
                const ov = rastro ? undefined : overlay?.get(a.id);
                const dr = !rastro && !ov?.out ? drive?.get(a.id) : undefined;
                const desde = dr?.prev == null ? '' : dr.prevKind === 'start' ? (dr.prev === 'office' ? t('opt.office') : t('opt.home')) : dr.prev;
                const fantasma = esFantasma(a.id) || !!rastro;
                const ovClass = rastro ? ' opt-moved-from' : fantasma ? ' opt-added' : ov ? (ov.out ? ' opt-out' : ' opt-changed') : '';
                return (
                  <div key={a.id} role="button" tabIndex={0} className={`db-ev${ovClass}${puedeArrastrar && !fantasma ? ' db-ev-movable' : ''}${preview?.id === a.id ? ' db-ev-drag' : ''}`}
                    title={puedeArrastrar && !fantasma ? [a.address, t('dnd.hint')].filter(Boolean).join('\n') : (a.address ?? undefined)}
                    style={{ top, height, minHeight: height, background: c.bg, borderLeftColor: c.ink, color: c.ink, outlineColor: ov?.color,
                    left: `calc(${lane * anchoPct}% + 3px)`, right: 'auto', width: `calc(${anchoPct}% - 6px)` }}
                    onPointerDown={(e) => { if (!fantasma) empezarArrastre(e, a, name); }}
                    onClick={(e) => { if (suprimirClick.current) return; if (!fantasma && !compact) onOpen(e, a); }} onKeyDown={(e) => { if (e.key === 'Enter' && !fantasma && !compact) onOpen(e as unknown as React.MouseEvent, a); }}>
                    <div className="db-ev-time">
                      {time(a.scheduled_start)}
                      {dr?.driveMin != null && (
                        <span className={`db-ev-drive${dr.slackMin != null && dr.slackMin < 0 ? ' late' : ''}`} title={dr.slackMin != null && dr.slackMin < 0 ? t('opt.lateBy', { min: String(Math.round(-dr.slackMin)) }) : undefined}>
                          🚗 {Math.round(dr.driveMin)}{dr.estimated ? '~' : ''} min
                        </span>
                      )}
                      <span className="db-ev-type">{TYPE_LABEL[a.type] || a.type}</span>
                    </div>
                    <div className="db-ev-client"><span>{w ? w.icon : <Home size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />} </span>{a.client_name || a.address?.split(',')[0] || t('cr.visit')}</div>
                    {rastro && <div><span className="opt-badge" style={{ background: overlay?.get(a.id.slice(5))?.color }}>→ {[rastro.rastroA, rastro.rastroHora ? time(rastro.rastroHora) : ''].filter(Boolean).join(' · ')}</span></div>}
                    {fantasma && !rastro && <div><span className="opt-badge" style={{ background: ov?.color }}>{t('opt.newBadge')}</span></div>}
                    {ov && !fantasma && (
                      <div><span className="opt-badge" style={{ background: ov.color }}>
                        {ov.out ? t('opt.removedBadge') : [
                          ov.from && ov.from !== a.inspector_name ? t('opt.fromLabel', { name: ov.from }) : '',
                          ov.scheduled_start && Date.parse(ov.scheduled_start) !== Date.parse(ov.fromStart) ? t('opt.wasAt', { time: time(ov.fromStart) }) : '',
                        ].filter(Boolean).join(' · ')}
                      </span></div>
                    )}
                    {a.contractor_name && !a.inspector_name && <div className="db-ev-sub"><Wrench size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {a.contractor_name}</div>}
                    {a.address && <div className="db-ev-sub">{a.address}</div>}
                    {dr?.driveMin != null && (
                      <div className="db-ev-sub db-ev-drivefrom">
                        🚗 {Math.round(dr.driveMin)} min{dr.miles != null ? ` · ${Math.round(dr.miles)} mi` : ''} {t('opt.fromPrev', { prev: desde })}
                        {dr.slackMin != null && dr.slackMin < 0 ? ` · ⚠ ${t('opt.lateBy', { min: String(Math.round(-dr.slackMin)) })}` : ''}
                      </div>
                    )}
                    {!fantasma && !compact && <div className="db-ev-actions">
                      <button type="button" className="db-ev-reassign" onClick={(e) => { e.stopPropagation(); setReassign(a); }}>⇄ {t('cr.reassign')}</button>
                      {a.claim_id && <button type="button" className="db-ev-reassign" onClick={(e) => { e.stopPropagation(); setRescheduleUrgent(false); setReschedule(a); }}>⟳ {t('sch.rescheduleShort')}</button>}
                      {a.claim_id && <button type="button" className="db-ev-reassign db-ev-urgent" title={t('sch.urgentHint')} onClick={(e) => { e.stopPropagation(); setRescheduleUrgent(true); setReschedule(a); }}>🚨 {t('sch.urgentReschedule')}</button>}
                      <button type="button" className="db-ev-reassign db-ev-remove" disabled={removing === a.id} onClick={(e) => { e.stopPropagation(); removeAppt(a); }}><Trash2 size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sch.remove')}</button>
                    </div>}
                  </div>
                );
                });
              })()}
            </div>
          );
        })}
      </div>
      {drop && <DropConfirm drop={drop} onClose={() => { setDrop(null); setPreview(null); }} onDone={() => { setDrop(null); setPreview(null); onChanged?.(); }} />}
      {reassign && <ReassignModal appt={reassign} onClose={() => setReassign(null)} onDone={() => { setReassign(null); onChanged?.(); }} />}
      {reschedule && reschedule.claim_id && (
        <ScheduleModal claimId={reschedule.claim_id} appointmentId={reschedule.id} mode="reschedule" urgent={rescheduleUrgent}
          title={reschedule.client_name || reschedule.address?.split(',')[0] || undefined}
          onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); onChanged?.(); }} />
      )}
    </div>
  );
}

type ROpt = { name: string; visitsThatDay: number; current: boolean };
type ReassignResp = { options: ROpt[]; currentKind?: string; currentContractor?: string | null; claimContractorName?: string | null };
function ReassignModal({ appt, onClose, onDone }: { appt: GridAppt; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [opts, setOpts] = useState<ROpt[]>([]);
  const [busy, setBusy] = useState(false);
  // Contratista (texto libre, como ClaimRoute) + "No inspector". Prellena con el contratista del claim.
  const [kind, setKind] = useState<'inspector' | 'contractor' | 'none'>('inspector');
  const [contractor, setContractor] = useState('');
  useEffect(() => {
    api.get<ReassignResp>(`/api/appointments/${appt.id}/reassign-options`).then((r) => {
      setOpts(r.options);
      setContractor(r.currentContractor || r.claimContractorName || '');
      if (r.currentKind === 'contractor') setKind('contractor');
      else if (r.currentKind === 'none') setKind('none');
    }).catch(() => setOpts([]));
  }, [appt.id]);
  const pickInspector = async (name: string) => {
    setBusy(true);
    try { await api.post(`/api/appointments/${appt.id}/reassign`, { assigneeKind: 'inspector', inspectorName: name }); onDone(); }
    catch { setBusy(false); }
  };
  const confirmAlt = async () => {
    if (kind === 'contractor' && !contractor.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/appointments/${appt.id}/reassign`, kind === 'contractor'
        ? { assigneeKind: 'contractor', contractorName: contractor.trim() }
        : { assigneeKind: 'none' });
      onDone();
    } catch { setBusy(false); }
  };
  return (
    <div className="calx-pop-back" onClick={onClose}>
      <div className="db-reassign" onClick={(e) => e.stopPropagation()}>
        <div className="db-reassign-head"><b>{t('cr.reassignTo')}</b><button className="calx-pop-x" onClick={onClose}><X size={14} strokeWidth={1.75} /></button></div>
        <div className="db-reassign-sub">{appt.client_name || appt.address || t('cr.visit')} · {time(appt.scheduled_start)}</div>
        <div className="db-reassign-list">
          {opts.map((o) => (
            <button key={o.name} className={`db-reassign-opt${o.current && kind === 'inspector' ? ' current' : ''}`} disabled={busy || (o.current && kind === 'inspector')} onClick={() => pickInspector(o.name)}>
              <span>{o.name}</span>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{o.current && kind === 'inspector' ? t('cr.current') : `${o.visitsThatDay} ${o.visitsThatDay === 1 ? t('cr.visit1') : t('cr.visitN')}`}</span>
            </button>
          ))}
          {opts.length === 0 && <div className="cr-empty">{t('common.loadingDots')}</div>}
        </div>
        {/* Alternativas: contratista (texto libre) o sin inspector — como ClaimRoute. */}
        <div className="db-reassign-alt">
          <label className={`db-reassign-altrow${kind === 'contractor' ? ' sel' : ''}`}>
            <input type="radio" name="ra-kind" checked={kind === 'contractor'} onChange={() => setKind('contractor')} />
            <span><Wrench size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sch.kind.contractor')}</span>
            <input className="input" style={{ marginLeft: 'auto', maxWidth: 160, fontSize: 'var(--fs-sm)' }} placeholder={t('sch.contractorName')} value={contractor}
              onChange={(e) => { setContractor(e.target.value); setKind('contractor'); }} />
          </label>
          <label className={`db-reassign-altrow${kind === 'none' ? ' sel' : ''}`}>
            <input type="radio" name="ra-kind" checked={kind === 'none'} onChange={() => setKind('none')} />
            <span><Ban size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('sch.kind.none')}</span>
          </label>
          {kind !== 'inspector' && (
            <button className="btn primary sm" style={{ marginTop: 6 }} disabled={busy || (kind === 'contractor' && !contractor.trim())} onClick={confirmAlt}>
              {busy ? t('common.loadingDots') : t('cr.reassignTo')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Lo que se soltó: a qué columna y a qué hora, y qué cambió respecto de donde estaba. */
type DropPedido = { a: GridAppt; col: string; startIso: string; durMin: number; cambiaHora: boolean; cambiaInsp: boolean };

/**
 * La confirmación al soltar (dueño 2026-10-29). Se escribe por la MISMA puerta que los botones de la cita:
 *  · cambió la hora (con o sin inspector) → `POST /reschedule { mode: 'now' }`, que además manda el SMS al
 *    homeowner y deja el rastro en Scheduling (por eso se avisa acá antes de confirmar);
 *  · cambió solo el inspector → `POST /reassign`, sin SMS (la hora es la misma).
 * Antes de confirmar pregunta `check-slot` (avisa, no bloquea), igual que el modal.
 */
function DropConfirm({ drop, onClose, onDone }: { drop: DropPedido; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const { a, col, startIso, cambiaHora, cambiaInsp } = drop;
  const inspectorDestino = cambiaInsp ? col : a.inspector_name;
  useEffect(() => {
    if (!a.claim_id || !inspectorDestino) return;
    let vivo = true;
    api.post<{ ok: boolean; reason: string | null; conflict: boolean }>(`/api/claims/${a.claim_id}/check-slot`, { inspectorName: inspectorDestino, startIso, type: a.type || null })
      .then((f) => { if (vivo && !f.ok) setAviso(`${f.conflict ? t('sch.conflictWarn') : t('sch.tightWarn')}${f.reason ? ` ${f.reason}` : ''}`); })
      .catch(() => { /* best-effort */ });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.id, startIso, inspectorDestino]);
  async function confirmar() {
    setBusy(true); setErr(null);
    try {
      if (cambiaHora) {
        await api.post(`/api/appointments/${a.id}/reschedule`, { mode: 'now', startIso, inspectorName: cambiaInsp ? col : null, reason: 'rescheduled', durationMin: drop.durMin });
      } else {
        await api.post(`/api/appointments/${a.id}/reassign`, { assigneeKind: 'inspector', inspectorName: col });
      }
      onDone();
    } catch (e) { setErr((e as Error).message || 'error'); setBusy(false); }
  }
  return (
    <div className="calx-pop-back" onClick={onClose}>
      <div className="db-reassign" onClick={(e) => e.stopPropagation()}>
        <div className="db-reassign-head"><b>{t('dnd.title')}</b><button className="calx-pop-x" onClick={onClose}><X size={14} strokeWidth={1.75} /></button></div>
        <div className="db-reassign-sub">{a.client_name || a.address?.split(',')[0] || t('cr.visit')} · {TYPE_LABEL[a.type] || a.type}</div>
        <div style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-sm)' }}>
          {cambiaHora && <div>{t('dnd.time', { from: time(a.scheduled_start), to: time(startIso) })}</div>}
          {cambiaInsp && <div>{t('dnd.inspector', { from: a.inspector_name || a.contractor_name || t('cr.unassigned'), to: col })}</div>}
          {cambiaHora && a.claim_id && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('dnd.sms')}</div>}
          {aviso && <div style={{ color: 'var(--orange)', fontSize: 'var(--fs-2xs)' }}>⚠ {aviso}</div>}
          {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn ghost sm" disabled={busy} onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn primary sm" disabled={busy} onClick={confirmar}>{busy ? t('dnd.moving') : t('dnd.move')}</button>
        </div>
      </div>
    </div>
  );
}
